import { Component, Inject, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import Swal from "sweetalert2";
import { IAcaoPlanoAcaoModel, IPlanoAcaoModel } from "../../../models/plano-acao-model";
import { PlanoAcaoService } from "../../../services/plano-acao.service";
import { PlanoAcaoFormAcaoComponent } from "./plano-acao-form-acao/plano-acao-form-acao.component";
import { PlanoAcaoFormAcaoAvaliadaComponent } from "./plano-acao-form-acao-avaliada/plano-acao-form-acao-avaliada.component";
import { PlanoAcaoMonitorMonitorarAcaoComponent } from "../../plano-acao-monitor/plano-acao-monitor-monitorar-acao/plano-acao-monitor-monitorar-acao.component";
import { PlanoAcaoFormHistoricoAcaoComponent } from "../plano-acao-form-preencher/plano-acao-form-historico-acao/plano-acao-form-historico-acao.component";
import { AuthService } from "src/app/services/auth.service";
import { EnumRecursoPerfil } from "src/app/models/enum/recurso-perfil.enum";
import { EnumAcaoRecurso } from "src/app/models/enum/acao-recurso.enum";
import { PlanoAcaoFormLogAlteracoesComponent } from "../plano-acao-log/plano-acao-form-log-alteracoes.component";

@Component({
  selector: 'app-plano-acao-form',
  templateUrl: './plano-acao-form.component.html',
  styleUrls: ['./plano-acao-form.component.scss']
})
export class PlanoAcaoFormComponent implements OnInit, AfterViewInit {

  public id: number = 0;
  public dataSource = new MatTableDataSource<IAcaoPlanoAcaoModel>([]);
  public visualizar: boolean = false;
  public titulo: string = "";

  public model: IPlanoAcaoModel = {} as IPlanoAcaoModel;

  public podeFinalizarPermissao = false;
  public podeEnviarAprovacaoPermissao = false;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    public matDialog: MatDialog,
    public servicePlanoAcao: PlanoAcaoService,
    private authService: AuthService,
    private dialogRef: MatDialogRef<PlanoAcaoFormAcaoComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data
  ) {
    this.id = data?.id || 0;
    this.visualizar = data?.visualizar || false;
    this.titulo = data?.titulo || '';
  }

  ngOnInit(): void {
    this.setarPermissoes();
    this.buscar();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
  }

  private setarPermissoes(): void {
    this.podeFinalizarPermissao = this.authService.possuiPermissao(EnumRecursoPerfil.PlanoDeAcao, EnumAcaoRecurso.Finalizar);
    this.podeEnviarAprovacaoPermissao = this.authService.possuiPermissao(EnumRecursoPerfil.PlanoDeAcao, EnumAcaoRecurso.EnviarParaAprovacao);
  }

  public sortingDataAccessor = (item, property) => {
    switch (property) {
      case "identificador": return item?.identificador;
      case "titulo": return item?.titulo;
      case "prazo": return item?.prazo;
      case "avaliacao": return item?.avaliacao;
      case "responsavel": return item?.responsavelNome;
      case "status": return item?.status;
      default: return item[property];
    }
  };

  private toast(icon: 'success' | 'warning', text: string) {
    Swal.fire({
      toast: true, position: "top-end", showConfirmButton: false,
      timer: 5000, timerProgressBar: true, icon, text,
    });
  }

  public buscar() {
    this.servicePlanoAcao
      .obterPorId(this.id)
      .then((res) => {
        if (res.sucesso) {
          this.model = res.dados;
          this.dataSource.data = this.model.acoes || [];

          this.cdr.detectChanges();
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
        }
      })
      .catch((err) => {
        this.toast("warning", err?.mensagem?.descricao || 'Erro ao carregar dados');
      });
  }

  // ---------- Regras de habilitação ----------

  get mostrarFinalizarElaboracao(): boolean {
    return !this.visualizar && this.model?.status?.codigo === 1 && !this.model?.elaboracaoFinalizada;
  }

  get habilitarFinalizarElaboracao(): boolean {
    return this.podeFinalizarPermissao && (this.model?.acoes?.length > 0);
  }

  get mostrarEnviarAprovacao(): boolean {
    return !this.visualizar && this.model?.status?.codigo === 1 && this.model?.elaboracaoFinalizada;
  }

  // Finalizar do plano Avaliado (envia para execução): ações todas Canceladas(5) ou A ser Realizada(6)
  get podeFinalizar(): boolean {
    const acoes = this.model?.acoes;
    if (!acoes || acoes.length === 0) return false;
    return acoes.every(t => t.status?.codigo === 5 || t.status?.codigo === 6);
  }

  // ---------- Critério 4: reabertura da elaboração ----------

  // true = pode prosseguir
  private async confirmarReabertura(): Promise<boolean> {
    if (this.model?.status?.codigo !== 1 || !this.model?.elaboracaoFinalizada) { return true; }

    const conf = await Swal.fire({
      title: "Atenção",
      text: "Este plano de ação encontra-se finalizado. Se prosseguir com a ação o plano precisará ser finalizado novamente. Deseja prosseguir?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Não",
    });
    if (!conf.value) { return false; }

    const res = await this.servicePlanoAcao.reabrirElaboracao(this.model.id);
    if (!res.sucesso) {
      this.toast("warning", res.mensagem.descricao);
      return false;
    }
    this.model.elaboracaoFinalizada = false;
    return true;
  }

  // ---------- Modais ----------

  public async adicionarAcao(titulo: string) {
    if (!(await this.confirmarReabertura())) { return; }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.id = "modal-plano-acao-acao";
    dialogConfig.width = "70vw";
    dialogConfig.maxHeight = "180vh";
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      titulo,
      visualizar: false,
      planoAcaoId: this.model.id,
      unidadeGestoraId: this.model.unidadeGestoraId,
      osaExecucaoId: this.model.osaExecucaoId,
      planoStatus: this.model.status?.codigo,
      id: 0
    };

    const modal = this.matDialog.open(PlanoAcaoFormAcaoComponent, dialogConfig);
    modal.afterClosed().subscribe(() => this.buscar());
  }

  public async modalAcao(titulo: string, visualizar: boolean, element?: IAcaoPlanoAcaoModel) {
    if (!visualizar && !(await this.confirmarReabertura())) { return; }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.id = "modal-plano-acao-acao";
    dialogConfig.width = "70vw";
    dialogConfig.maxHeight = "180vh";
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      titulo,
      visualizar,
      id: element?.id,
      planoAcaoId: this.model.id,
      unidadeGestoraId: this.model.unidadeGestoraId,
      osaExecucaoId: this.model.osaExecucaoId,
      planoStatus: this.model.status?.codigo,
      element
    };

    const modal = this.matDialog.open(PlanoAcaoFormAcaoComponent, dialogConfig);
    modal.afterClosed().subscribe(() => this.buscar());
  }

  public async modalAcaoAvaliada(titulo: string, visualizar: boolean, element?: IAcaoPlanoAcaoModel) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.id = "modal-plano-acao-acao-avaliada";
    dialogConfig.width = "70vw";
    dialogConfig.maxHeight = "180vh";
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      titulo,
      visualizar,
      id: element?.id,
      planoAcaoId: this.model.id,
      unidadeGestoraId: this.model.unidadeGestoraId,
      osaExecucaoId: this.model.osaExecucaoId,
      element
    };

    const modal = this.matDialog.open(PlanoAcaoFormAcaoAvaliadaComponent, dialogConfig);
    modal.afterClosed().subscribe((data) => {
      if (data) { this.buscar(); }
    });
  }

  public modalMonitoramento(titulo: string, visualizar: boolean, element?: IAcaoPlanoAcaoModel) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.id = "modal-plano-acao-acao-monitoramento";
    dialogConfig.width = "70vw";
    dialogConfig.maxHeight = "180vh";
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      titulo,
      visualizar,
      id: element?.id,
      planoAcaoId: this.model.id,
      unidadeGestoraId: this.model.unidadeGestoraId,
      osaExecucaoId: this.model.osaExecucaoId,
      element
    };

    const modal = this.matDialog.open(PlanoAcaoMonitorMonitorarAcaoComponent, dialogConfig);
    modal.afterClosed().subscribe((data) => {
      if (data) { this.buscar(); }
    });
  }

  // public modalHistorico(element?: IAcaoPlanoAcaoModel) {
  //   const dialogConfig = new MatDialogConfig();
  //   dialogConfig.id = "modal-plano-acao-acao-historico";
  //   dialogConfig.width = "70vw";
  //   dialogConfig.maxHeight = "140vh";
  //   dialogConfig.hasBackdrop = true;
  //   dialogConfig.disableClose = true;
  //   dialogConfig.data = { id: element?.id, identificador: element?.identificador, idFuncionalidade: 41 };

  //   const modal = this.matDialog.open(PlanoAcaoFormHistoricoAcaoComponent, dialogConfig);
  //   modal.afterClosed().subscribe((data) => {
  //     if (data) { this.buscar(); }
  //   });
  // }

  // ---------- Ações ----------

  public async excluir(element: IAcaoPlanoAcaoModel) {
    if (!(await this.confirmarReabertura())) { return; }

    Swal.fire({
      title: "Excluir a Ação",
      text: "Tem certeza que deseja excluir a ação?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Não",
    }).then((res) => {
      if (res.value) {
        this.servicePlanoAcao.excluirAcao(element.id).then((r) => {
          if (r.sucesso) {
            this.buscar();
            this.toast("success", r.mensagem.descricao);
          } else {
            this.toast("warning", r.mensagem.descricao);
          }
        });
      } else {
        // usuário desistiu da exclusão depois de reabrir: recarrega o estado real
        this.buscar();
      }
    });
  }

  public fechar() {
    this.dialogRef.close(!this.visualizar);
  }

  public finalizarElaboracao() {
    this.servicePlanoAcao.finalizarElaboracao(this.model.id).then((res) => {
      this.toast(res.sucesso ? "success" : "warning", res.mensagem.descricao);
      if (res.sucesso) { this.buscar(); }
    });
  }

  public enviarParaAprovacao(id: number) {
    this.servicePlanoAcao.enviarParaAprovacao(id).then((res) => {
      if (res.sucesso) {
        this.dialogRef.close(true);
        this.toast("success", res.mensagem.descricao);
      } else {
        this.toast("warning", res.mensagem.descricao);
      }
    });
  }

  // Finalizar do plano Avaliado (envia para execução)
  public finalizar(id: number) {
    this.servicePlanoAcao.enviarParaExecucao(id).then((res) => {
      if (res.sucesso) {
        this.dialogRef.close(true);
        this.toast("success", res.mensagem.descricao);
      } else {
        this.toast("warning", res.mensagem.descricao);
      }
    });
  }

  public modalHistorico(element?: IAcaoPlanoAcaoModel) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.id = "modal-plano-acao-acao-log-alteracoes";
    dialogConfig.width = "900px";
    dialogConfig.maxHeight = "95vh";
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = true;
    dialogConfig.data = { id: element?.id, identificador: element?.identificador, titulo: element?.titulo };

    this.matDialog.open(PlanoAcaoFormLogAlteracoesComponent, dialogConfig);
  }
}